/**
 * Cova Chat — layered tagged errors. Each layer maps the layer-below's errors
 * to its own tagged error: Repository → ChatDbError; Service → ChatServiceError
 * (+ the specific domain errors below); Presentation → ChatRpcError.
 *
 * PII: never place message body or filenames in any error — ids/counts/operation
 * only (logging-guidelines.md).
 */
import { Schema as S } from "effect"

// ============================================================================
// Infrastructure
// ============================================================================

/** Any SQL failure in a chat repository. */
export class ChatDbError extends S.TaggedError<ChatDbError>()("ChatDbError", {
  message: S.String,
  cause: S.Unknown,
  operation: S.String,
}) {}

// ============================================================================
// Application
// ============================================================================

/** A repo error or otherwise-unexpected failure surfaced by a chat service. */
export class ChatServiceError extends S.TaggedError<ChatServiceError>()("ChatServiceError", {
  message: S.String,
  cause: S.Unknown,
  operation: S.String,
}) {}

/** The conversation does not exist, or the caller is not a participant (do not
 *  leak existence — surfaces as RPC `not_found`). */
export class ConversationNotFound extends S.TaggedError<ConversationNotFound>()(
  "ConversationNotFound",
  { message: S.String, conversationId: S.String },
) {}

/** The caller does not service any mailbox in the conversation (RPC `not_allowed`). */
export class NotAuthorisedToService extends S.TaggedError<NotAuthorisedToService>()(
  "NotAuthorisedToService",
  { message: S.String },
) {}

/** The caller's org may not initiate (not a brokerage, or self-message /
 *  same-org-both-sides) — surfaces as RPC `not_allowed`. */
export class NotAuthorisedToInitiate extends S.TaggedError<NotAuthorisedToInitiate>()(
  "NotAuthorisedToInitiate",
  { message: S.String },
) {}

/** The addressing target (e.g. underwriter id) does not resolve (RPC `not_found`). */
export class TargetNotFound extends S.TaggedError<TargetNotFound>()("TargetNotFound", {
  message: S.String,
  targetId: S.String,
}) {}

/** `UploadService.completeUpload` failed — the message is never created; any
 *  completed nodes are reclaimed by the files lifecycle (RPC `internal`). */
export class AttachmentUploadError extends S.TaggedError<AttachmentUploadError>()(
  "AttachmentUploadError",
  { message: S.String, cause: S.Unknown },
) {}

/** The caller is not a participant of the group conversation (RPC `not_allowed`). */
export class NotGroupMember extends S.TaggedError<NotGroupMember>()("NotGroupMember", {
  message: S.String,
}) {}

/** The caller is not an owner/admin (nor org admin) of the group (RPC `not_allowed`). */
export class NotGroupAdmin extends S.TaggedError<NotGroupAdmin>()("NotGroupAdmin", {
  message: S.String,
}) {}

/** Removing/leaving would leave the group with no owner — blocked atomically
 *  by the last-owner guard (RPC `not_allowed`). */
export class CannotRemoveLastOwner extends S.TaggedError<CannotRemoveLastOwner>()(
  "CannotRemoveLastOwner",
  { message: S.String },
) {}

/** A mentioned user is not a participant of the conversation — rolls back the
 *  send transaction (RPC `not_allowed`). */
export class InvalidMention extends S.TaggedError<InvalidMention>()("InvalidMention", {
  message: S.String,
}) {}

/** The call does not exist (RPC `not_found`). */
export class CallNotFound extends S.TaggedError<CallNotFound>()("CallNotFound", {
  message: S.String,
  callId: S.String,
}) {}

/** The caller is not a participant of the call's conversation (RPC `not_allowed`). */
export class NotCallParticipant extends S.TaggedError<NotCallParticipant>()(
  "NotCallParticipant",
  { message: S.String },
) {}

/** A lifecycle transition was attempted on an already-terminal call (RPC `not_allowed`). */
export class CallAlreadyEnded extends S.TaggedError<CallAlreadyEnded>()("CallAlreadyEnded", {
  message: S.String,
  callId: S.String,
}) {}

/** startCall on a space/group thread — calls are 1:1, spaces get meetings.
 *  Server-side gate behind the UI-only direct-thread affordance (RPC `not_allowed`). */
export class NotADirectConversation extends S.TaggedError<NotADirectConversation>()(
  "NotADirectConversation",
  { message: S.String },
) {}

/** startCall resolved zero people to ring — nothing to do, and no Chime room is
 *  minted (RPC `not_allowed`). */
export class NoCallRecipients extends S.TaggedError<NoCallRecipients>()("NoCallRecipients", {
  message: S.String,
}) {}

/** joinCall without a pending invite, or inviteToCall targeting someone who is
 *  not an addressable same-org teammate (RPC `not_allowed`). */
export class NotInvitedToCall extends S.TaggedError<NotInvitedToCall>()("NotInvitedToCall", {
  message: S.String,
  callId: S.String,
}) {}

/**
 * Scheduling refusals (scheduling spec 2026-07-26-001 §14). All are DOMAIN
 * refusals with a safe message — never internal detail — so the handler can pass
 * them through the wire-error union rather than mapping them to `internal`.
 */

/** Cancel/reschedule attempted on a meeting that is already live or over — end
 *  it instead of cancelling it (RPC `not_allowed`). */
export class MeetingAlreadyStarted extends S.TaggedError<MeetingAlreadyStarted>()(
  "MeetingAlreadyStarted",
  {
    message: S.String,
    meetingId: S.String,
  },
) {}

/** Only the organiser may cancel or reschedule (RPC `not_allowed`). Being a
 *  participant is enough to SCHEDULE, but not to change someone else's meeting. */
export class NotMeetingOrganiser extends S.TaggedError<NotMeetingOrganiser>()(
  "NotMeetingOrganiser",
  {
    message: S.String,
    meetingId: S.String,
  },
) {}

/** A time in the past, or a duration outside 1–480 minutes (RPC `invalid_input`).
 *  Carries `field` so the UI can attach the error to the right control. */
export class InvalidSchedule extends S.TaggedError<InvalidSchedule>()("InvalidSchedule", {
  message: S.String,
  field: S.String,
}) {}

/** Recording was requested before every human participant consented (RPC `not_allowed`). */
export class RecordingConsentMissing extends S.TaggedError<RecordingConsentMissing>()(
  "RecordingConsentMissing",
  { message: S.String },
) {}

// ─── Space meetings ──────────────────────────────────────────────────────────
// NOTE: these domain errors carry NO `cause` — they must never be blanket-mapped
// into ChatServiceError, or toRpc misclassifies them as `internal` (500). The
// service narrows ONLY infra errors (ChatDbError / ChimeError) at each tail.

/** The meeting does not exist (RPC `not_found`). */
export class MeetingNotFound extends S.TaggedError<MeetingNotFound>()("MeetingNotFound", {
  message: S.String,
  meetingId: S.String,
}) {}

/** The caller is not a participant of the meeting's space (RPC `not_allowed`). */
export class NotMeetingParticipant extends S.TaggedError<NotMeetingParticipant>()(
  "NotMeetingParticipant",
  { message: S.String },
) {}

/** A host-only action (ending the meeting for everyone) was attempted by an
 *  attendee. Distinct from NotMeetingParticipant: the caller IS in the space,
 *  they just didn't start this meeting (RPC `not_allowed`). */
export class NotMeetingHost extends S.TaggedError<NotMeetingHost>()("NotMeetingHost", {
  message: S.String,
}) {}

/** Join/attend was attempted on a meeting that isn't live, or that has no Chime
 *  session yet (RPC `not_allowed`). */
export class MeetingNotLive extends S.TaggedError<MeetingNotLive>()("MeetingNotLive", {
  message: S.String,
  meetingId: S.String,
}) {}

/** Meetings are a SPACE feature — a 1:1 direct thread must use Calls (RPC `not_allowed`). */
export class NotASpaceConversation extends S.TaggedError<NotASpaceConversation>()(
  "NotASpaceConversation",
  { message: S.String },
) {}

// ============================================================================
// Presentation
// ============================================================================

/**
 * The single error the RPC boundary returns. Service/authz errors are mapped
 * here via a typed `toRpc(_tag)` switch. `reason` drives the client's UX;
 * `rate_limited` is reserved for the deferred numeric-rate-limit fast-follow.
 */
export class ChatRpcError extends S.TaggedError<ChatRpcError>()("ChatRpcError", {
  message: S.String,
  // `invalid_input` is the one reason the USER can act on by editing what they
  // typed (a past meeting time, an out-of-range duration), so the UI can attach
  // it to a field instead of showing a generic failure. Additive: existing
  // consumers compare against specific values and have a default branch.
  reason: S.optional(
    S.Literal("not_found", "not_allowed", "rate_limited", "invalid_input", "internal"),
  ),
  operation: S.optional(S.String),
  /**
   * The id that ties this refusal to the server log line that explains it
   * (ruling 40, spec section 4.5). Optional, because every ChatRpcError minted
   * before this field existed carries none and none of them needs one: it is
   * filled where the refusal is real but unexplainable to the reader, starting
   * with an attachment that could not be opened because the record of the
   * download could not be written. Support reads the id back to the log rather
   * than asking the person what they clicked.
   */
  traceId: S.optional(S.String),
}) {}
